import React from 'react';
import { SELECTION_COLUMN_ID } from 'ag-grid-community';
import GlobalStyles from '@mui/material/GlobalStyles';
import { GRID_SERVICE_KEYS } from '@/components/AgGrid/helpers';
import {
  IS_OPEN_CONTEXT_MENU_CLASS,
  ROW_HAS_ERROR,
  ROW_HAS_WARNING,
  ROW_PUBLISH_SCHEDULING,
} from '@/components/AgGrid/Documents/agGridThemeClasses';

const {
  PREVIEW_CHECKBOX_COLUMN_NAME,
  PREVIEW_BTN_COLUMN_NAME,
  COLUMNS_VISIBILITY_MENU,
  CONTEXT_MENU_COLUMN_NAME,
  COLUMNS_LINKED_ICON,
} = GRID_SERVICE_KEYS;

type Props = {
  // left pinned cell which starts the row rounded border; 2 - when first column is hidden context menu column
  firstPinnedCellIndex?: number;
};

const AgGridDocumentsStyles = ({ firstPinnedCellIndex = 2 }: Props) => {
  return (
    <GlobalStyles
      styles={(theme) => {
        const isRtl = theme.direction === 'rtl';
        const LEFT = isRtl ? 'right' : 'left';
        const RIGHT = isRtl ? 'left' : 'right';

        return {
          '.ag-theme-dokka': {
            // Page-size selector sits 50px from the left with the page-nav pushed right
            // (margin-right:auto). Moved here from AgGridGlobalStyles (DA-19459) so only Workspace +
            // JEInsights keep this layout; other grids fall back to AG Grid's default right-aligned
            // paging panel.
            '& .ag-paging-panel .ag-paging-page-size': {
              margin: '0 auto 0 50px',
            },

            '& .ag-overlay': {
              // Only the overlay content takes clicks. The overlay box covers the whole grid, and since v36 the header
              // sits in the scrolling viewport, which paints under it, so no z-index on the header can lift it above.
              // A clickable box would block the header over an empty grid.
              '& .ag-react-container': {
                pointerEvents: 'all',
                justifyContent: 'center',
                // Without this, the wrapper sizes to content and breaks
                // height-based @container queries inside the overlay component.
                height: '100%',
                width: '100%',

                '& .MuiDivider-root': {
                  display: 'none',
                },
              },
            },

            '& .ag-selection-checkbox': {
              marginLeft: theme.spacing(1),
              marginRight: 0,
            },

            [`& .ag-icon.ag-icon-${isRtl ? 'first' : 'last'}`]: {
              display: 'none',
            },

            // ======== STYLES FOR HEADER PINNED COLUMNS
            '& .ag-header': {
              [`& .ag-grid-pinned-${LEFT}-cells`]: {
                '& .ag-header-cell': {
                  [`&:nth-child(${firstPinnedCellIndex})`]: {
                    borderRadius: '8px 0 0 8px',
                    border: '1px solid var(--ag-border-color)',
                    borderRight: 'none',
                  },
                },
              },

              // v36 draws the pinned header edge on the container's inner wrapper
              [`& .ag-grid-pinned-${RIGHT}-cells > .ag-grid-container-wrapper`]: {
                border: 'none',
              },
            },

            // v36 pins columns by making their cells sticky over the scrolling ones, and paints the pinned
            // containers to hide what scrolls under them (white in the header, the transparent data
            // background in rows). The hidden context-menu column leaves an empty slot there; paint the
            // containers with the page colour so that slot looks like the page and never shows scrolled
            // cells, as it did on 34 where the scrolling area ended at the pinned edge.
            '& .ag-header-row, & .ag-row': {
              '& > .ag-grid-pinned-left-cells, & > .ag-grid-pinned-right-cells': {
                backgroundColor: theme.palette.background.default,
                backgroundImage: 'none',

                // the header's inner wrapper paints the header colour itself; its cells paint their own
                '& > .ag-grid-container-wrapper': {
                  backgroundColor: 'transparent',
                },
              },
            },

            '& .ag-header-cell': {
              [`&.${COLUMNS_VISIBILITY_MENU}`]: {
                padding: theme.spacing(0, 0.5),
                borderRadius: '0 8px 8px 0',
                border: '1px solid var(--ag-border-color) !important',
              },

              [`&.${CONTEXT_MENU_COLUMN_NAME}`]: {
                display: 'none',
              },

              [`&.${PREVIEW_CHECKBOX_COLUMN_NAME}`]: {
                flexDirection: 'row-reverse',
              },

              '& .ag-header-select-all': {
                margin: 0,
              },
            },

            '& .ag-cell': {
              [`&.${COLUMNS_VISIBILITY_MENU}`]: {
                borderRight: '1px solid var(--ag-border-color) !important',
                borderRadius: '0 8px 8px 0',
              },

              [`&.${CONTEXT_MENU_COLUMN_NAME}`]: {
                opacity: 0,
                border: 0,
                backgroundColor: 'transparent',

                // context menu icon stay visible when menu is open
                [`&.${IS_OPEN_CONTEXT_MENU_CLASS}`]: {
                  opacity: '1!important',
                },
              },

              [`&.${PREVIEW_CHECKBOX_COLUMN_NAME}`]: {
                paddingLeft: 8,

                '& > .ag-cell-wrapper': {
                  flexDirection: 'row-reverse',
                },
              },

              [`&[col-id="${SELECTION_COLUMN_ID}"],
            &[col-id="${PREVIEW_CHECKBOX_COLUMN_NAME}"],
            &[col-id="${PREVIEW_BTN_COLUMN_NAME}"],
            &[col-id="${CONTEXT_MENU_COLUMN_NAME}"],
            &[col-id="${COLUMNS_LINKED_ICON}"],
            &[col-id="${COLUMNS_VISIBILITY_MENU}"]`]: {
                '& .ag-range-handle': {
                  width: 0,
                },
              },
            },

            '& .ag-cell-expandable': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: 2,
            },

            // 40px holds the checkbox only without the global cell padding and the checkbox margin
            [`& .ag-cell[col-id="${SELECTION_COLUMN_ID}"], & .ag-header-cell[col-id="${SELECTION_COLUMN_ID}"]`]: {
              paddingLeft: 0,
              paddingRight: 0,

              '& .ag-cell-wrapper, & .ag-header-cell-comp-wrapper': {
                justifyContent: 'center',
              },

              '& .ag-selection-checkbox, & .ag-header-select-all': {
                margin: 0,
              },

              // the column has no value and no header label; their empty elements would take the space
              // beside the checkbox
              '& .ag-cell-wrapper > .ag-cell-value, & .ag-header-cell-comp-wrapper': {
                display: 'none',
              },
            },

            [`& .ag-header-cell[col-id="${SELECTION_COLUMN_ID}"]`]: {
              justifyContent: 'center',
            },

            // the expanded master row is marked on its first cell, the expander
            '& .ag-row-group-expanded': {
              [`& .${PREVIEW_CHECKBOX_COLUMN_NAME}`]: {
                paddingLeft: 0,

                '& > .ag-cell-wrapper': {
                  borderLeft: `3px solid ${theme.palette.common.black}`,
                  borderRadius: '4px 0 0 4px',
                },
              },
            },

            // // ======== STYLES FOR ROW
            '& .ag-group-contracted, .ag-group-expanded': {
              margin: '0 !important',
            },

            '& .ag-row': {
              '&.ag-row-level-1': {
                backgroundColor: 'var(--ag-background-color) !important',
                width: 'calc(100% - 70px)',
                maxHeight: 390,
                marginLeft: 60,
                borderLeft: `3px solid ${theme.palette.common.black}`,
                outline: '1px solid var(--ag-border-color)',
                borderRadius: 8,
              },
              [`&.${ROW_HAS_ERROR}`]: {
                '& .ag-cell': {
                  backgroundColor: '#f4e3e5',
                },
                '&.ag-row-hover .ag-cell': {
                  backgroundColor: '#f3dbdd',
                },
                '&.ag-row-selected .ag-cell': {
                  backgroundColor: '#f3dbdd',
                },
                '& .ag-cell.ag-cell-range-selected:not(.ag-column-hover)': {
                  '&:after': {
                    content: '""',
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    backgroundColor: 'var(--ag-range-selection-background-color ,rgba(122,134,203,.1)) !important',
                  },
                },
              },

              [`&.${ROW_HAS_WARNING}`]: {
                '& .ag-cell': {
                  backgroundColor: '#f7e9e2',
                },
                '&.ag-row-hover .ag-cell': {
                  backgroundColor: '#f7e4da',
                },
                '&.ag-row-selected .ag-cell': {
                  backgroundColor: '#f7e4da',
                },
                '& .ag-cell.ag-cell-range-selected:not(.ag-column-hover)': {
                  '&:after': {
                    content: '""',
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    backgroundColor: 'var(--ag-range-selection-background-color ,rgba(122,134,203,.1)) !important',
                  },
                },
              },

              [`&.${ROW_PUBLISH_SCHEDULING}`]: {
                '& .ag-cell': {
                  backgroundColor: '#e7e9ec',
                  color: theme.palette.text.disabled,
                },
                '&.ag-row-hover .ag-cell': {
                  backgroundColor: '#e3e5e7',
                },
                '&.ag-row-selected .ag-cell': {
                  backgroundColor: '#e3e5e7',
                },
                '& .ag-cell.ag-cell-range-selected:not(.ag-column-hover)': {
                  '&:after': {
                    content: '""',
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    backgroundColor: 'var(--ag-range-selection-background-color ,rgba(122,134,203,.1)) !important',
                  },
                },
              },

              '&.ag-row-hover': {
                '&:before': {
                  display: 'none',
                },

                '& .ag-cell': {
                  [`&.${CONTEXT_MENU_COLUMN_NAME}`]: {
                    opacity: 1,
                    backgroundColor: 'transparent',
                  },
                },
              },
            },

            // v36 renders each row's pinned cells inside the row, sticky rows included
            [`& .ag-row .ag-grid-pinned-${LEFT}-cells`]: {
              '& .ag-cell': {
                [`&:nth-child(${firstPinnedCellIndex})`]: {
                  borderRadius: '8px 0 0 8px',
                  border: '1px solid var(--ag-border-color)',
                  borderRight: 'none',
                },
              },
            },
          },
        };
      }}
    />
  );
};

export default AgGridDocumentsStyles;
